import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  ACCEPTANCE_LANGUAGE,
  DEFAULT_LANGUAGE,
  WITHOUT_NOTIFICATION_HEADER,
} from '@core/constants';
import {
  PaymentBalacingConfiguration,
  PaymentBalacingPayment,
  PaymentBalacingPaymentStatus,
  PaymentBalacingSSE,
  PaymentBalacingStatus,
  PaymentBalacingStatusEvent,
  PaymentCardModel,
  PaymentsCardResponseModel,
  WithoutNotificationHeader,
} from '@core/models';
import { ExternalSessionState } from '@core/state/external';
import { SessionState } from '@core/state/shared';
import { camelizeData } from '@core/utils';
import { environment } from '@env/environment';
import { __VERSION__ } from '@rcsa-frontend/__version__';
import { BlockUIService } from 'ng-block-ui';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PaymentsBalancingService {
  public static blockUiName = 'payments-balancing-block-ui';
  public componentRefresh$: Subject<void> = new Subject();
  public configuration$: BehaviorSubject<PaymentBalacingConfiguration> =
    new BehaviorSubject<PaymentBalacingConfiguration>({
      location: 'guardians-app',
    });
  public status$: BehaviorSubject<PaymentBalacingStatusEvent>;
  private activePaymentBalancingStatusId = 0;
  private lastCall: {
    configuration: PaymentBalacingConfiguration;
    data: PaymentCardModel;
    idempotencyKey: string;
  };
  private paymentId: string;
  private tries: number;

  constructor(
    private blockUIService: BlockUIService,
    private externalSessionState: ExternalSessionState,
    private http: HttpClient,
    private sessionState: SessionState,
    private router: Router
  ) {}

  public closePayment() {
    this.blockUIService.stop(PaymentsBalancingService.blockUiName);
    this.status$.next({
      id: this.activePaymentBalancingStatusId,
      status: PaymentBalacingStatus.COMPLETED_ERROR,
    });
    this.status$.complete();
  }

  public finishPayment() {
    this.blockUIService.stop(PaymentsBalancingService.blockUiName);
    this.status$.next({
      id: this.activePaymentBalancingStatusId,
      paymentId: this.paymentId,
      status: PaymentBalacingStatus.COMPLETED,
    });
    this.status$.complete();
  }

  public pay(
    data: PaymentCardModel,
    idempotencyKey: string,
    configuration?: PaymentBalacingConfiguration
  ) {
    this.configuration$.next({
      location: 'guardians-app',
      ...(configuration || {}),
    });
    this.activePaymentBalancingStatusId++;
    this.lastCall = {
      configuration: this.configuration$.value,
      data,
      idempotencyKey,
    };

    this.status$ = new BehaviorSubject<PaymentBalacingStatusEvent>({
      id: this.activePaymentBalancingStatusId,
      status: PaymentBalacingStatus.LOADING,
    });
    this.componentRefresh$.next();

    this.blockUIService.start(PaymentsBalancingService.blockUiName);
    this.startPayment(data, idempotencyKey).subscribe({
      next: (response) => {
        this.paymentId = response.id;
        this.tries = 0;
        this.status$.next({
          ...this.status$.value,
          processors: response.processors.map((processor) => processor.name),
        });

        this.listenSSEStatusChanges(this.activePaymentBalancingStatusId);
      },
      error: () => {
        this.status$.next({
          error: 'api-error',
          id: this.activePaymentBalancingStatusId,
          status: PaymentBalacingStatus.ERROR,
        });
      },
    });
    return this.status$;
  }

  public retryPayment() {
    this.blockUIService.stop(PaymentsBalancingService.blockUiName);
    return this.pay(
      this.lastCall.data,
      this.lastCall.idempotencyKey,
      this.lastCall.configuration
    );
  }

  private getLocationBaseURL() {
    return this.configuration$.value.location === 'externals-app'
      ? '/external'
      : '';
  }

  private getPaymentStatus() {
    return this.http.get<{ data: PaymentBalacingPayment; message: string }>(
      `${
        environment.apiUrl
      }/v1${this.getLocationBaseURL()}/payments/payment-management/cards/multiple/asynchronous/${
        this.paymentId
      }`,
      {
        headers: {
          [WITHOUT_NOTIFICATION_HEADER]: WithoutNotificationHeader.ALL,
        },
      }
    );
  }

  private listenEndpointStatusChanges(activePaymentBalancingStatusId: number) {
    const intervalKiller = setInterval(() => {
      this.getPaymentStatus().subscribe({
        next: (response) => {
          const payment: PaymentBalacingPayment = response.data;
          this.onNewPaymentStatus(
            payment.asyncStatus,
            () => {
              clearInterval(intervalKiller);
            },
            activePaymentBalancingStatusId
          );
        },
        error: () => {
          this.tries++;
          if (this.tries > 20) {
            if (
              activePaymentBalancingStatusId ===
              this.activePaymentBalancingStatusId
            ) {
              this.status$.next({
                data: this.status$.value.data,
                error: 'status-error',
                id: activePaymentBalancingStatusId,
                processors: this.status$.value.processors,
                status: PaymentBalacingStatus.ERROR,
              });
            }
            clearInterval(intervalKiller);
          }
        },
      });
    }, 5000);
  }

  private listenSSEStatusChanges(activePaymentBalancingStatusId: number) {
    this.tries++;
    if (this.tries <= 5) {
      const token = this.router.url.startsWith('/externos/')
          ? this.externalSessionState.accessToken
          : this.sessionState.accessToken,
        sse = new EventSourcePolyfill(
          `${
            environment.apiUrl
          }/v1${this.getLocationBaseURL()}/payments/sse?payment_id=${
            this.paymentId
          }`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-App-Version': __VERSION__,
              'Accept-Language':
                ACCEPTANCE_LANGUAGE.get(this.sessionState.country) ||
                DEFAULT_LANGUAGE,
            },
          }
        );
      sse.onerror = (e: { error?: any; status?: number }) => {
        if (e.error || e.status) {
          sse.close();
          this.listenSSEStatusChanges(activePaymentBalancingStatusId);
        }
      };
      sse.onmessage = (e: { data: string }) => {
        const event: PaymentBalacingSSE = camelizeData(JSON.parse(e.data));
        this.onNewPaymentStatus(
          event.payment.asyncStatus,
          () => sse.close(),
          activePaymentBalancingStatusId
        );
      };
    } else {
      this.listenEndpointStatusChanges(activePaymentBalancingStatusId);
    }
  }

  private modifyLastPayment(newStatus: PaymentBalacingPaymentStatus) {
    const data = [...(this.status$.value.data || [])],
      lastElement = data.length > 0 ? data[data.length - 1] : null;
    if (lastElement) {
      data[data.length - 1] = { ...data[data.length - 1], status: newStatus };
    }
    return data;
  }

  private onNewPaymentStatus(
    asyncStatus: string,
    terminate: () => void,
    activePaymentBalancingStatusId: number
  ) {
    if (
      activePaymentBalancingStatusId !== this.activePaymentBalancingStatusId
    ) {
      return;
    }
    if (
      asyncStatus === '100' ||
      asyncStatus === '101' ||
      asyncStatus === '102'
    ) {
      this.status$.next({
        data: [
          {
            processor: 'Memphis',
            status: PaymentBalacingPaymentStatus.PROCESSING,
          },
        ],
        id: activePaymentBalancingStatusId,
        processors: this.status$.value.processors,
        status: PaymentBalacingStatus.LOADING,
      });
    } else if (
      asyncStatus === '103' ||
      asyncStatus === '111' ||
      asyncStatus === '112'
    ) {
      this.status$.next({
        data: [
          {
            processor: 'Memphis',
            status: PaymentBalacingPaymentStatus.REJECTED,
          },
          {
            processor: 'Kushki',
            status: PaymentBalacingPaymentStatus.PROCESSING,
          },
        ],
        id: activePaymentBalancingStatusId,
        processors: this.status$.value.processors,
        status: PaymentBalacingStatus.LOADING,
      });
    } else if (asyncStatus === '200') {
      this.status$.next({
        data: this.modifyLastPayment(PaymentBalacingPaymentStatus.PAID),
        id: activePaymentBalancingStatusId,
        processors: this.status$.value.processors,
        status: PaymentBalacingStatus.SUCCESS,
      });
      terminate();
    } else if (asyncStatus === '400') {
      this.status$.next({
        data: this.modifyLastPayment(PaymentBalacingPaymentStatus.REJECTED),
        error: 'declined',
        id: activePaymentBalancingStatusId,
        processors: this.status$.value.processors,
        status: PaymentBalacingStatus.ERROR,
      });
      terminate();
    } else if (asyncStatus === '505') {
      this.status$.next({
        data: this.modifyLastPayment(PaymentBalacingPaymentStatus.REJECTED),
        error: 'processing-error',
        id: activePaymentBalancingStatusId,
        status: PaymentBalacingStatus.ERROR,
      });
      terminate();
    }
  }

  private startPayment(
    data: PaymentCardModel,
    idempotencyKey: string
  ): Observable<PaymentsCardResponseModel> {
    const headers = new HttpHeaders({
      'Idempotency-Key': idempotencyKey,
    });
    return this.http
      .post(
        `${
          environment.apiUrl
        }/v1${this.getLocationBaseURL()}/payments/payment-management/cards/multiple/asynchronous`,
        data,
        {
          headers,
        }
      )
      .pipe(map((response: any) => response.data));
  }
}
